import { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { CampLocation, CampExperience, GeoPoint } from "@/types/campLocation";
import { formSchema } from "../schemas/locationFormSchema";
import { haveFormValuesChanged, deepClone } from "@/utils/formUtils";

export const useLocationFormInit = (location: CampLocation | null) => {
  const [isSaving, setIsSaving] = useState(false);
  const [changeDescription, setChangeDescription] = useState("");
  const [isDirty, setIsDirty] = useState(false);
  const [manualSave, setManualSave] = useState(false);
  const [initialValues, setInitialValues] = useState<any>(null);
  
  // Create a safe location object to work with
  const safeLocation = location || {
    id: '',
    name: '',
    visibility: 'v_draft' as const,
    address: { country: 'USA' },
    doesWaitlist: null,
    doesScholarships: null
  };

  // Initialize default values for new fields if they don't exist in the location object
  const religiousAffiliationKeys = safeLocation.religiousAffiliationKeys || [];
  const religiousAffiliationNotes = safeLocation.religiousAffiliationNotes || "";
  const allergyKeys = safeLocation.allergyKeys || [];
  const allergyNotes = safeLocation.allergyNotes || "";
  const supportForKeys = safeLocation.supportForKeys || [];
  const supportForNotes = safeLocation.supportForNotes || "";
  
  // Initialize default campExperiences or use existing ones with all required fields
  const defaultExperience: CampExperience = {
    programName: "Default",
    topActivities: "",
    outdoorTimeScale: 50,
    structuredTimeScale: 50,
    physicalityScale: 50,
    themeMainKey: "",
    themeOtherKeys: [],
    hideScales: false,
  };

  // Process any existing campExperiences to ensure themeOtherKeys is always an array
  const campExperiences: CampExperience[] = safeLocation.campExperiences?.map(exp => {
    return {
      ...exp,
      programName: exp.programName || "Default",
      topActivities: exp.topActivities || "",
      outdoorTimeScale: exp.outdoorTimeScale ?? 50,
      structuredTimeScale: exp.structuredTimeScale ?? 50,
      physicalityScale: exp.physicalityScale ?? 50,
      themeMainKey: exp.themeMainKey || "",
      themeOtherKeys: Array.isArray(exp.themeOtherKeys) ? exp.themeOtherKeys : [],
      hideScales: exp.hideScales ?? false,
    };
  }) || [defaultExperience];

  // Convert latLng to string for form display if it's a GeoPoint
  let latLngString = "";
  if (safeLocation.address?.latLng) {
    if (typeof safeLocation.address.latLng === 'string') {
      latLngString = safeLocation.address.latLng;
    } else if (safeLocation.address.latLng instanceof GeoPoint) {
      // Format GeoPoint as "latitude, longitude" string
      latLngString = `${safeLocation.address.latLng.latitude}, ${safeLocation.address.latLng.longitude}`;
    } else if (
      typeof safeLocation.address.latLng === 'object' && 
      safeLocation.address.latLng !== null &&
      'latitude' in safeLocation.address.latLng && 
      'longitude' in safeLocation.address.latLng
    ) {
      // Handle when it's an object with latitude and longitude properties
      const geoLoc = safeLocation.address.latLng as { latitude: number; longitude: number };
      latLngString = `${geoLoc.latitude}, ${geoLoc.longitude}`;
    }
  }

  // Define form schema default values without the savedStatus property
  const formDefaultValues = {
    name: safeLocation.name || "",
    urlMain: safeLocation.urlMain || "",
    searchResultExcerpt: safeLocation.searchResultExcerpt || "",
    descLocation: safeLocation.descLocation || "",
    descTimingAndBooking: safeLocation.descTimingAndBooking || "",
    address: {
      address1: safeLocation.address?.address1 || "",
      address2: safeLocation.address?.address2 || "",
      city: safeLocation.address?.city || "",
      state: safeLocation.address?.state || "",
      zip: safeLocation.address?.zip || "",
      country: safeLocation.address?.country || "USA",
      latLng: latLngString,
      webUrl: safeLocation.address?.webUrl || "",
      localityLabel: safeLocation.address?.localityLabel || "",
    },
    doesWaitlist: safeLocation.doesWaitlist ?? null,
    doesScholarships: safeLocation.doesScholarships ?? null,
    scholarshipNotes: safeLocation.scholarshipNotes || "",
    
    // Fields for About Your Camp tab
    religiousAffiliationKeys,
    religiousAffiliationNotes,
    allergyKeys,
    allergyNotes,
    supportForKeys,
    supportForNotes,
    campExperiences,
    
    // Pricing fields
    regularOfferIncrementKey: safeLocation.regularOfferIncrementKey || "",
    incrementPrice: safeLocation.incrementPrice || null,
    priceNote: safeLocation.priceNote || "",
    singleDayOption: safeLocation.singleDayOption || false,
    singleDayPrice: safeLocation.singleDayPrice || null,
    
    // Add changeDescription field
    changeDescription: "",
  };

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: formDefaultValues,
  });

  // Store the initial values to compare for dirty state detection
  useEffect(() => {
    if (!initialValues) {
      const formInitialValues = deepClone(form.getValues());
      setInitialValues(formInitialValues);
      console.log("Initial form values stored:", JSON.stringify(formInitialValues).substring(0, 200) + "...");
    }
  }, [form, initialValues]);

  // Enhanced form change detection - using both react-hook-form's isDirty and deep comparison
  useEffect(() => {
    // Watch for ANY changes in the form values
    const subscription = form.watch((formValues) => {
      if (!initialValues) return; // Skip if initialValues aren't set yet
      
      const currentValues = form.getValues();
      
      // Use form's built-in isDirty for immediate detection
      const formIsDirty = form.formState.isDirty;
      
      // Also perform deep comparison for more accurate detection
      const valuesChanged = initialValues && haveFormValuesChanged(currentValues, initialValues);
      
      // Log detailed information about change detection
      console.log("Form change detected:", {
        fieldChanged: formValues,
        formIsDirty: formIsDirty,
        valuesChanged: valuesChanged
      });
      
      // Set dirty if either method detects changes
      if (formIsDirty || valuesChanged) {
        console.log("Setting form as dirty due to detected changes");
        setIsDirty(true);
      }
    });
    
    // Cleanup subscription on component unmount
    return () => subscription.unsubscribe();
  }, [form, initialValues]);

  // Reset form if location changes
  useEffect(() => {
    if (location) {
      console.log(`Form reset with location data: ${location.name} (${location.id})`);
      // Reset the form and the dirty tracking snapshot so we don't
      // immediately flag the form as dirty when switching locations
      form.reset(formDefaultValues);
      setIsDirty(false);
      setInitialValues(deepClone(formDefaultValues));
    }
  }, [location?.id]);

  return {
    form,
    isSaving,
    setIsSaving,
    changeDescription,
    setChangeDescription,
    isDirty,
    setIsDirty,
    manualSave,
    setManualSave,
    initialValues,
    setInitialValues,
  };
};
